import React, {useEffect, useState} from 'react';
import {nanoid} from 'nanoid';
import FormControl from '@material-ui/core/FormControl';
import Box from '@material-ui/core/Box';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {REPORT_TYPES} from 'helpers/constants/reports';
import {
  FEATURE_RESTRICTION_IDS,
  STRIPE_ACCOUNT_RESTRICTION_NAMES
} from 'helpers/constants/restrictions';

export default function ReportsTypePicker({reportType, setReportType}) {
  const handleChange = (event) => {
    setReportType(event.target.value);
  };
  const {
    AllowedServiceFeatures,
    StripeAccountType,
    hasReceivedSurveys,
    IsByocOnly,
    isByocOnly: isByocOnlyCamel
  } = useSelector((state) => state.user.activeLocation);

  const isByocOnly = IsByocOnly === true || isByocOnlyCamel === true;

  const defaultOptions = [
    {
      name: 'Self Delivery Summary',
      type: 3,
      customVisible: (features) =>
        (features.includes(FEATURE_RESTRICTION_IDS.BYOC) ||
          features.includes(FEATURE_RESTRICTION_IDS.SELF_DELIVERY_REPORT)) &&
        !features.includes(FEATURE_RESTRICTION_IDS.BLOCK_SELF_DELIVERY_REPORT)
    },
    {
      name: 'Self Delivery Invoices',
      type: REPORT_TYPES.SELF_DELIVERY_INVOICES,
      customVisible: (features) =>
        (features.includes(FEATURE_RESTRICTION_IDS.BYOC) ||
          features.includes(FEATURE_RESTRICTION_IDS.SELF_DELIVERY_REPORT)) &&
        !features.includes(
          FEATURE_RESTRICTION_IDS.BLOCK_SELF_DELIVERY_REPORT
        ) &&
        features.includes(FEATURE_RESTRICTION_IDS.SELF_DELIVERY_INVOICES_REPORT)
    },
    {
      name: `${
        AllowedServiceFeatures?.includes(FEATURE_RESTRICTION_IDS.BYOC)
          ? 'Direct '
          : ''
      }Sales Summary`,
      type: 0,
      featureFlag: FEATURE_RESTRICTION_IDS.SALES_REPORTS,
      hideWhenByocOnly: true,
      stripeAccountRestriction:
        STRIPE_ACCOUNT_RESTRICTION_NAMES.CONNECTED_STANDARD
    },
    {
      name: 'Orders By Channel Summary',
      type: REPORT_TYPES.SALES_BREAKDOWN_SUMMARY,
      featureFlags: [
        FEATURE_RESTRICTION_IDS.BYOC,
        FEATURE_RESTRICTION_IDS.SALES_BREAKDOWN_REPORT
      ]
    },
    {
      name: 'Customers Summary',
      type: 1,
      featureFlag: FEATURE_RESTRICTION_IDS.CUSTOMERS_REPORTS
    },
    {
      name: `${
        AllowedServiceFeatures?.includes(FEATURE_RESTRICTION_IDS.BYOC)
          ? 'Direct '
          : ''
      }Payout Summary`,
      type: 2,
      customVisible: () => !isByocOnly
    },
    // {name: 'Book a Driver', type: 4},
    {
      name: 'Refunds Summary',
      type: 5,
      featureFlag: FEATURE_RESTRICTION_IDS.REFUNDS_REPORTS,
      stripeAccountRestriction:
        STRIPE_ACCOUNT_RESTRICTION_NAMES.CONNECTED_STANDARD
    },
    {
      name: 'Reviews Summary',
      type: 6,
      featureFlag: FEATURE_RESTRICTION_IDS.FEEDBACK && !hasReceivedSurveys
    },
    {
      name: 'Special Orders',
      type: 7,
      featureFlag: FEATURE_RESTRICTION_IDS.SPECIAL_ORDERS_REPORT
    },
    {
      name: 'Book A Driver Summary',
      type: 8,
      featureFlag: FEATURE_RESTRICTION_IDS.BOOK_A_DRIVER_AUTO_CHARGE
    },
    {
      name: 'Discount Summary',
      type: 9,
      hideWhenByocOnly: true
    },
    {
      name: 'Cashback Summary',
      type: 10,
      featureFlag: FEATURE_RESTRICTION_IDS.CASHBACK
    }
  ];

  const [options, setOptions] = useState([]);

  useEffect(() => {
    const filtered = defaultOptions.filter((item) => {
      // Check single feature flag
      const hasFeatureFlag =
        !item.featureFlag || AllowedServiceFeatures.includes(item.featureFlag);

      // Check multiple feature flags (if featureFlags array exists)
      const hasFeatureFlags =
        !item.featureFlags ||
        item.featureFlags.every((flag) =>
          AllowedServiceFeatures.includes(flag)
        );

      const isExcludedByFeatureFlag =
        item.excludeWhenFeatureFlag &&
        AllowedServiceFeatures.includes(item.excludeWhenFeatureFlag);

      const hasCustomVisible =
        !item.customVisible || item.customVisible(AllowedServiceFeatures);

      const isHiddenForByocOnly = item.hideWhenByocOnly && isByocOnly;

      return (
        hasFeatureFlag &&
        hasFeatureFlags &&
        !isExcludedByFeatureFlag &&
        hasCustomVisible &&
        !isHiddenForByocOnly &&
        (!item.stripeAccountRestriction ||
          item.stripeAccountRestriction !== StripeAccountType)
      );
    });
    setOptions(filtered);
    if (!filtered.some((opt) => opt.type === reportType)) {
      setReportType(filtered[0]?.type ?? 0);
    }
  }, [AllowedServiceFeatures, isByocOnly]);

  return (
    <Box sx={{minWidth: 120}} className='Say2EatDrop'>
      <FormControl fullWidth variant='outlined'>
        <Select
          labelId='demo-simple-select-label'
          id='demo-simple-select'
          value={reportType}
          onChange={handleChange}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left'
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left'
            },
            getContentAnchorEl: null
          }}
        >
          {options.map((item) => (
            <MenuItem
              className='reportTypeItem'
              key={nanoid()}
              value={item.type}
              disableRipple
            >
              {item.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

ReportsTypePicker.propTypes = {
  reportType: PropTypes.number.isRequired,
  setReportType: PropTypes.func.isRequired
};
